/**
 * connectTileRehydrate.ts — the connect card is DATA; its html is a projection.
 *
 * P0 thread-durability (docs/PLAN-non-direct-app-connections-20260901.md
 * §3.3, D6 mechanism (b)): the browser-lane connect card posts with its ENTIRE
 * content in `workflowActionButtonsHtml` and a `payload` that already
 * snapshots everything the html was built from (`data.requestedApp`,
 * `data.research`). The whole-array thread doc's byte-budget backstop blanks
 * the OLDEST large card html (`cardHtmlDroppedForFirestore:true`) and the
 * localStorage quota last-resort used to drop it too — after which the
 * message had no text, no html and no card, was filed as a silent record and
 * silently skipped at render. Nothing re-derived the html from the payload.
 *
 * This module is that re-derivation, in three lanes:
 *   1. PURE repair (`rebuildDroppedCardHtml`) — a card whose html is gone but
 *      whose payload is a known kind gets its html rebuilt by the SAME builder
 *      that posted it (byte-identical today), with `isContextOnly` cleared so
 *      the render lane shows it again. Wired at the chat reducer's
 *      SET_MESSAGES boundary (useGlobalChat.ts), the ONE seam every hydrate
 *      path already crosses — server page, IDB mirror, thread switch,
 *      load-earlier prepend, cross-tab sync, and both CHAT_HISTORY restores —
 *      so a stripped copy is repaired before it can reach the screen, with no
 *      network and no flash.
 *   2. ASYNC refresh (`rehydrateConnectTile`) — at hydrate, every connect card
 *      peeks the workspace's custom-connection roster and re-renders in place
 *      with its CURRENT state ("signed in as …", "session expired", "opened
 *      1:55 PM — sign-in not finished"). Never posts a new message: the
 *      re-summon path belongs to the primary (MASTER RULE) — this lane only
 *      re-projects what already stands in the thread.
 *   3. LIFECYCLE stamp (`stampConnectTileOpened`) — the browser-lane click
 *      writes `payload.data.lifecycle.opened` onto the card message through
 *      `updateMessageById` (contract C2), so a day-old card reads as history
 *      instead of a DOM-only "opened ✓" that dies with the page.
 *
 * Status vocabulary is code-authored STATUS copy (D9 — the deterministic
 * honesty line, never product content), authored as {en, es}; the card body
 * itself comes from the builder. `finished` / `answered` stamps have NO
 * producer in P0 (the sign-in modal emits no page-level outcome event) — they
 * land with the P2 outcome events.
 *
 * Catalog-offer cards (`composio.connect_offer`) are NOT rebuildable from
 * today's payload: their html renders logo/description that `data.apps[]`
 * does not carry. They return null here — never a fabricated lookalike.
 *
 * Import graph note: this module reaches connectApp.ts (the builder) and is
 * imported statically ONLY by useGlobalChat.ts, whose graph already carries
 * the whole tool registry through useGlobalTools — nothing new is pulled into
 * the leaf hydrate helpers (useChatThreads.ts, threadPersist.ts).
 */

import type { ConnectResearch, CustomConnection } from '../../composio/ComposioClient';
import {
  UI_LANGUAGE_OVERRIDE_KEY,
  getUserUiLanguage,
  type UiLanguage,
} from '../../threadMetadata/uiLanguage';
import { CONNECT_APP_BUTTON_IDS, buildBrowserLaneCardHtml, connectAppOfferKey } from './connectApp';
// The authorize/governance card's ONE builder. A Firebase-free leaf on purpose:
// this module runs inside the chat reducer (P4, 2026-09-16 — see
// authorizeCardHtml.ts's header).
import {
  buildAuthorizeCardHtml,
  governanceCardView,
  type GovernanceCardView,
} from './authorizeCardHtml';
import type { PendingActionPreview } from '../../prest0nSession/client';
// The desktop-run card's ONE builder (P6b, 2026-09-20) — a Firebase-free leaf
// for the same reason authorizeCardHtml.ts is: it is called from the reducer.
import {
  DESKTOP_RUN_CARD_KIND,
  buildDesktopRunCardHtml,
  desktopRunCardDataOf,
} from './desktopRunCardHtml';
// The voice-session card's ONE builder (avatar experience PR-B, 2026-09-21) —
// a Firebase-free leaf for the same reason.
import {
  VOICE_SESSION_CARD_KIND,
  buildVoiceSessionCardHtml,
  voiceSessionCardDataOf,
} from './voiceSessionCardHtml';

/** payload.kind values this module can re-project from `payload.data`. */
export const REBUILDABLE_CONNECT_CARD_KINDS: ReadonlySet<string> = new Set([
  'composio.connect_unavailable',
  // P4: the approval card Prest0n posts into an approver's own thread. Its
  // html is a projection of `data.preview`, which the SERVER wrote — so a
  // thread whose card html was dropped by the byte-budget backstop still shows
  // the approver a decision they can act on, at every hydrate, forever.
  'prest0n.authorize_offer',
  // P6b (2026-09-20): a computer run's "show me on the desktop page" card. Its
  // html is a projection of the session id and outcome the SERVER wrote into
  // the result's signal — so a thread whose card html was dropped by the
  // byte-budget backstop still lands the person on the right screen, at every
  // hydrate, forever.
  DESKTOP_RUN_CARD_KIND,
  // Avatar experience PR-B (2026-09-21): the one card a spoken session leaves
  // in the thread. The SERVER writes only the payload (the exchanges as spoken);
  // the html is projected here at every hydrate, so the card is never blank
  // after the byte-budget backstop and never a lookalike.
  VOICE_SESSION_CARD_KIND,
]);

/** Contract C2: lifecycle stamps live INSIDE payload.data (persisted by every
 *  store lane), written only through updateMessageById after the post. */
export interface ConnectCardLifecycle {
  /** ISO — the browser-lane button was clicked. */
  opened?: string;
  /** ISO — reserved: no producer in P0 (derived from the roster at hydrate). */
  signedIn?: string;
  /** ISO — reserved: no producer in P0 (P2 outcome events). */
  finished?: string;
  /** Reserved: no producer in P0 (question pills are P1b). */
  answered?: string;
}

export type ConnectCardPayloadLike = {
  kind?: string;
  data?: Record<string, unknown>;
} | null | undefined;

/** Minimal message shape the pure helpers read — structural so ChatMessage
 *  (and plain persisted records) pass without an import cycle. */
export interface ConnectCardMessageLike {
  id?: string;
  isUser?: boolean;
  sourceTag?: string;
  payload?: ConnectCardPayloadLike;
  workflowActionButtonsHtml?: string;
  isContextOnly?: boolean;
  cardHtmlDroppedForFirestore?: boolean;
  cardHtmlRebuilt?: boolean;
}

/** The roster verdict for the card's app, or null when the roster could not
 *  be read (offline / denied) — unknown, never assumed. */
export type ConnectRosterVerdict = {
  status: 'connected' | 'signin-lost' | 'registered' | 'absent';
  signedInAs?: string | null;
} | null;

export interface ConnectLifecycleStatus {
  key: 'signed-in' | 'session-expired' | 'opened' | 'opened-unfinished' | 'unfinished';
  text: string;
}

export interface ConnectTileMessaging {
  /** In-place re-render primitive (the batch-tile idiom). Absent → nothing
   *  can be rebuilt and the helpers report false. */
  updateMessageById?: (
    id: string,
    update: {
      workflowActionButtonsHtml?: string;
      isContextOnly?: boolean;
      payload?: ConnectCardPayloadLike;
    },
  ) => void;
}

function escapeHtml(text: string): string {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

function payloadData(payload: ConnectCardPayloadLike): Record<string, unknown> {
  const data = payload && typeof payload === 'object' ? payload.data : undefined;
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
}

function payloadKind(payload: ConnectCardPayloadLike): string {
  return payload && typeof payload === 'object' && typeof payload.kind === 'string' ? payload.kind : '';
}

function requestedAppOf(payload: ConnectCardPayloadLike): string {
  const raw = payloadData(payload).requestedApp;
  return typeof raw === 'string' ? raw.trim() : '';
}

/** A non-user message whose payload is a known connect kind carrying the app
 *  name the card was built with. */
export function isRebuildableConnectCard(m: ConnectCardMessageLike | null | undefined): boolean {
  if (!m || m.isUser) return false;
  const kind = payloadKind(m.payload);
  if (!REBUILDABLE_CONNECT_CARD_KINDS.has(kind)) return false;
  // A governance offer carries its own facts, never an app name.
  if (kind === 'prest0n.authorize_offer') return governanceOfferOf(m.payload) !== null;
  // A desktop-run card carries a session id; without one there is nothing to show.
  if (kind === DESKTOP_RUN_CARD_KIND) return desktopRunCardDataOf(m.payload) !== null;
  // A voice-session card carries the exchanges; without any there is nothing to show.
  if (kind === VOICE_SESSION_CARD_KIND) return voiceSessionCardDataOf(m.payload) !== null;
  return requestedAppOf(m.payload).length > 0;
}

/**
 * The governance offer this payload carries, or null. FAIL-CLOSED on every
 * axis: an EMAIL authorize offer (which snapshots only `to` + `subject`) has no
 * preview to rebuild from and returns null rather than a lookalike — its card
 * is re-fetched from the server by the marker lane, exactly as before.
 */
function governanceOfferOf(
  payload: ConnectCardPayloadLike,
): { actionId: string; preview: PendingActionPreview; view: GovernanceCardView } | null {
  const data = payloadData(payload);
  const actionId = typeof data.actionId === 'string' ? data.actionId.trim() : '';
  const raw = data.preview && typeof data.preview === 'object' ? (data.preview as PendingActionPreview) : null;
  if (!actionId || !raw || raw.kind !== 'rbac') return null;
  if (!String(raw.summary || raw.subject || '').trim()) return null;
  const declared = data.view === 'self' || data.view === 'approver' || data.view === 'waiting'
    ? (data.view as GovernanceCardView)
    : null;
  return { actionId, preview: raw, view: governanceCardView(raw, declared) };
}

/**
 * The browser-lane card's primary pill (`${CONNECT_APP_BUTTON_IDS.browser}-open`).
 * The click lands on the transcript's delegated handler, which is where the
 * `opened` stamp is written — that surface holds the card's message AND the
 * update dispatch. Prefix-matched so a future per-app suffix still stamps.
 */
export function isConnectBrowserOpenButtonId(buttonId: unknown): boolean {
  return typeof buttonId === 'string' && buttonId.startsWith(CONNECT_APP_BUTTON_IDS.browser);
}

/**
 * Re-project the card html from its persisted payload (contract C2's
 * `rebuildCardHtmlFromPayload`) — the SAME builder the post used, called the
 * SAME way, so the result is byte-identical to the original. The post is
 * `buildBrowserLaneCardHtml(appQuery, research, { lang, reconnect: !!reconnect,
 * slug: reconnect?.slug })` (connectApp.ts); every one of those arguments is
 * snapshotted in `payload.data` (contract C2), and every one is optional here
 * — an older message carrying only `requestedApp` rebuilds to the same card
 * the builder would produce for it. Returns null for any kind this module
 * cannot rebuild honestly.
 */
export function rebuildCardHtmlFromPayload(payload: ConnectCardPayloadLike): string | null {
  // TOTAL BY CONTRACT (2026-09-02 review). This runs INSIDE the chat reducer,
  // on `payload.data.research` — arbitrary JSON that round-tripped Firestore
  // and localStorage, from a server shape this module does not own. A throw
  // here is not one broken tile: it is every SET_MESSAGES for that thread
  // throwing, on every device, forever — the thread could never be opened
  // again, and the fix would have to reach the stored data. The card builder
  // lives in another bucket's file and is free to change; this belt is what
  // makes "the card is data, its html a projection" safe to run in the
  // reducer regardless. A card that cannot be re-projected degrades to the
  // history tile it already is.
  try {
    if (payloadKind(payload) === 'prest0n.authorize_offer') {
      const offer = governanceOfferOf(payload);
      if (!offer) return null;
      const html = buildAuthorizeCardHtml(offer.actionId, offer.preview, { view: offer.view });
      return typeof html === 'string' && html.trim() ? html : null;
    }
    if (payloadKind(payload) === DESKTOP_RUN_CARD_KIND) {
      const run = desktopRunCardDataOf(payload);
      if (!run) return null;
      const html = buildDesktopRunCardHtml(run);
      return typeof html === 'string' && html.trim() ? html : null;
    }
    if (payloadKind(payload) === VOICE_SESSION_CARD_KIND) {
      const session = voiceSessionCardDataOf(payload);
      if (!session) return null;
      const html = buildVoiceSessionCardHtml(session);
      return typeof html === 'string' && html.trim() ? html : null;
    }
    if (payloadKind(payload) !== 'composio.connect_unavailable') return null;
    const app = requestedAppOf(payload);
    if (!app) return null;
    const data = payloadData(payload);
    const research = data.research && typeof data.research === 'object'
      ? (data.research as ConnectResearch)
      : null;
    const lang: UiLanguage = data.lang === 'es' ? 'es' : 'en';
    const reconnect = data.alreadyConnected === true;
    const slug = typeof data.slug === 'string' && data.slug ? data.slug : undefined;
    // The key-lane offer is part of the snapshot (2026-09-03): a rehydrated
    // card that dropped it would be a card that quietly got worse on reload,
    // which is exactly what contract C2 exists to prevent.
    const keyToolkitSlug =
      typeof data.keyToolkitSlug === 'string' && data.keyToolkitSlug ? data.keyToolkitSlug : undefined;
    const html = buildBrowserLaneCardHtml(app, research, {
      lang,
      reconnect,
      ...(slug ? { slug } : {}),
      ...(keyToolkitSlug ? { keyToolkitSlug } : {}),
    });
    return typeof html === 'string' && html.trim() ? html : null;
  } catch (err) {
    warnRebuildFailure(payload, err);
    return null;
  }
}

const rebuildFailureWarned = new Set<string>();

/** One breadcrumb per payload shape per page session — diagnosable without
 *  turning a hydrate into a console flood. */
function warnRebuildFailure(payload: ConnectCardPayloadLike, err: unknown): void {
  try {
    const key = `${payloadKind(payload)}::${requestedAppOf(payload)}`;
    if (rebuildFailureWarned.has(key)) return;
    rebuildFailureWarned.add(key);
    // eslint-disable-next-line no-console
    console.warn('[connectTileRehydrate] card rebuild failed — tile stays history', {
      kind: payloadKind(payload),
      app: requestedAppOf(payload),
      error: err instanceof Error ? err.message : String(err),
    });
  } catch {
    // The breadcrumb is never load-bearing.
  }
}

/** Test seam: forget the once-per-session rebuild-failure breadcrumbs. */
export function __resetConnectRebuildWarningsForTests(): void {
  rebuildFailureWarned.clear();
}

/** Alias kept for readers coming from the plan's wording. */
export const rebuildConnectCardHtml = rebuildCardHtmlFromPayload;

// ── status vocabulary (D9 code-authored status copy, {en, es}) ──────────────
// Never mentions the product name; the card body (builder-owned) carries
// "Prest0n" with its capital P.
const STATUS_COPY: Record<UiLanguage, {
  signedIn: (who: string) => string;
  sessionExpired: string;
  opened: (when: string) => string;
  openedUnfinished: (when: string) => string;
  unfinished: string;
}> = {
  en: {
    signedIn: (who) => (who ? `signed in as ${who}` : 'signed in'),
    sessionExpired: 'session expired — sign in again',
    opened: (when) => `opened ${when}`,
    openedUnfinished: (when) => `opened ${when} — sign-in not finished`,
    unfinished: 'sign-in not finished',
  },
  es: {
    signedIn: (who) => (who ? `sesión iniciada como ${who}` : 'sesión iniciada'),
    sessionExpired: 'la sesión expiró — inicia sesión de nuevo',
    opened: (when) => `abierto ${when}`,
    openedUnfinished: (when) => `abierto ${when} — inicio de sesión sin terminar`,
    unfinished: 'inicio de sesión sin terminar',
  },
};

function parseStamp(value: unknown): Date | null {
  if (typeof value !== 'string' || !value) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** "1:55 PM" on the same calendar day; "Sep 1, 1:55 PM" otherwise. */
export function formatStampTime(stamp: Date, lang: UiLanguage, now: Date = new Date()): string {
  const locale = lang === 'es' ? 'es' : 'en-US';
  const sameDay = stamp.getFullYear() === now.getFullYear()
    && stamp.getMonth() === now.getMonth()
    && stamp.getDate() === now.getDate();
  try {
    return sameDay
      ? stamp.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
      : stamp.toLocaleString(locale, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return stamp.toISOString().slice(0, 16).replace('T', ' ');
  }
}

/**
 * The one status line a card shows, from what is KNOWN: the roster verdict
 * beats the click stamp (a finished sign-in is "signed in" even if the card
 * was opened a day earlier); an `opened` stamp with no roster verdict is
 * "opened <time>"; with a roster that says not connected it is "opened <time>
 * — sign-in not finished"; a registered-but-never-confirmed roster entry with
 * no stamp (cards from before the stamp shipped) is "sign-in not finished".
 * No stamp and no verdict → no line.
 */
export function connectLifecycleStatus(input: {
  lifecycle?: ConnectCardLifecycle | Record<string, unknown> | null;
  /** undefined = not consulted (pure lane); null = consulted but unreadable. */
  roster?: ConnectRosterVerdict;
  lang: UiLanguage;
  now?: Date;
}): ConnectLifecycleStatus | null {
  const copy = STATUS_COPY[input.lang === 'es' ? 'es' : 'en'];
  const roster = input.roster;
  if (roster && roster.status === 'connected') {
    const who = typeof roster.signedInAs === 'string' ? roster.signedInAs.trim() : '';
    return { key: 'signed-in', text: copy.signedIn(who) };
  }
  if (roster && roster.status === 'signin-lost') {
    return { key: 'session-expired', text: copy.sessionExpired };
  }
  const lifecycle = input.lifecycle && typeof input.lifecycle === 'object'
    ? (input.lifecycle as Record<string, unknown>)
    : {};
  const opened = parseStamp(lifecycle.opened);
  if (!opened) {
    if (roster && roster.status === 'registered') {
      return { key: 'unfinished', text: copy.unfinished };
    }
    return null;
  }
  const when = formatStampTime(opened, input.lang, input.now);
  if (roster && (roster.status === 'registered' || roster.status === 'absent')) {
    return { key: 'opened-unfinished', text: copy.openedUnfinished(when) };
  }
  return { key: 'opened', text: copy.opened(when) };
}

/**
 * Append the status row INSIDE the card's root element (before its closing
 * tag) so it inherits the card's scoped styling; a root without a closing
 * div gets the row appended after it. Rebuilds always start from the
 * builder's fresh html, so no previous row ever needs stripping.
 */
export function appendConnectLifecycleRow(cardHtml: string, status: ConnectLifecycleStatus | null): string {
  if (!status) return cardHtml;
  const row = `<div data-connect-lifecycle="${status.key}" style="margin-top:10px;font-size:0.78rem;line-height:1.45;color:var(--brand-text-secondary);">${escapeHtml(status.text)}</div>`;
  const close = cardHtml.lastIndexOf('</div>');
  if (close < 0) return `${cardHtml}${row}`;
  return `${cardHtml.slice(0, close)}${row}${cardHtml.slice(close)}`;
}

/** Marker the status row carries, so a projection can be recognised. */
export const LIFECYCLE_ROW_MARKER = 'data-connect-lifecycle';

// ── UI language, read ONCE per page session ────────────────────────────────
// The pure rebuild runs inside the chat reducer, and a reducer that reads
// localStorage on every pass is a reducer whose output depends on something
// other than its arguments (review 2026-09-02: reducers are double-invoked in
// React StrictMode). The fallback read is memoised here and invalidated by
// the storage event the override key would fire, so within a session the
// language term is a constant. A payload that carries its own `lang` never
// reaches this at all.
let uiLangMemo: UiLanguage | null = null;
let uiLangListenerBound = false;
function memoizedUiLanguage(): UiLanguage {
  if (!uiLangListenerBound && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    uiLangListenerBound = true;
    window.addEventListener('storage', (e: StorageEvent) => {
      if (!e || !e.key || e.key === UI_LANGUAGE_OVERRIDE_KEY) uiLangMemo = null;
    });
  }
  if (!uiLangMemo) uiLangMemo = getUserUiLanguage();
  return uiLangMemo;
}

/** Test seam: forget the memoised UI language. */
export function __resetConnectUiLanguageMemoForTests(): void {
  uiLangMemo = null;
}

/**
 * PURE hydrate repair. Every non-user message whose payload is a rebuildable
 * connect kind and whose card html is gone (blanked by the byte-budget
 * backstop, dropped by the quota last-resort, or filed as a data-only record)
 * gets its html re-projected from the payload, `isContextOnly` cleared, and
 * a deterministic `cardHtmlRebuilt` marker (the drop breadcrumb stays for
 * diagnostics). Returns the SAME array reference when nothing changed —
 * stable React identity, and byte-identical output for identical input so
 * the reducer's no-op bail still starves update cascades (a timestamp marker
 * would defeat that, which is why the marker is a boolean).
 *
 * IT ALSO RE-APPLIES A STAMPED STATUS ROW (review 2026-09-02). The `opened`
 * stamp lives in `payload.data.lifecycle` — persisted by every store lane —
 * but the row it renders lives only in the html, which the next server
 * snapshot replaces wholesale (both copies count as finalized, so the merge
 * guard keeps the incoming one). A card whose payload carries a lifecycle
 * stamp and whose html has lost the row is therefore re-projected here too,
 * so "opened 1:55 PM — sign-in not finished" survives every snapshot rather
 * than showing once and disappearing. The narrow gate — a lifecycle stamp
 * present AND the row absent — keeps the pure lane away from intact cards it
 * has no better projection for (an older payload could rebuild a THINNER
 * card than the one already stored).
 */
export function rebuildDroppedCardHtml<T extends ConnectCardMessageLike>(
  messages: T[],
  opts: { lang?: UiLanguage; now?: Date } = {},
): T[] {
  if (!Array.isArray(messages) || messages.length === 0) return messages;
  let out: T[] | null = null;
  for (let i = 0; i < messages.length; i += 1) {
    // TOTAL PER ROW (2026-09-02 review): this loop runs inside the chat
    // reducer, so one row that cannot be re-projected must cost that row its
    // card and nothing more. A throw escaping here breaks every SET_MESSAGES
    // for the thread — the transcript never renders again.
    try {
      const m = messages[i];
      if (!m || m.isUser) continue;
      const html = typeof m.workflowActionButtonsHtml === 'string' ? m.workflowActionButtonsHtml : '';
      if (!isRebuildableConnectCard(m)) continue;
      const data = payloadData(m.payload);
      const lifecycle = data.lifecycle && typeof data.lifecycle === 'object'
        ? (data.lifecycle as ConnectCardLifecycle)
        : undefined;
      const rowMissing = !html.includes(LIFECYCLE_ROW_MARKER);
      const stamped = !!(lifecycle && typeof lifecycle.opened === 'string' && lifecycle.opened);
      if (html.trim() && !(stamped && rowMissing)) continue;
      const base = rebuildCardHtmlFromPayload(m.payload);
      if (!base) continue;
      // The card's own language (the post-time snapshot) selects the status
      // row too, so a Spanish card never grows an English row; the current UI
      // setting is only the fallback for pre-C2 payloads that carry no lang.
      const lang: UiLanguage = data.lang === 'es' || data.lang === 'en'
        ? (data.lang as UiLanguage)
        : (opts.lang || memoizedUiLanguage());
      const status = connectLifecycleStatus({ lifecycle, lang, now: opts.now });
      const next = appendConnectLifecycleRow(base, status);
      if (next === html) continue;
      if (!out) out = messages.slice();
      out[i] = {
        ...m,
        workflowActionButtonsHtml: next,
        isContextOnly: false,
        cardHtmlRebuilt: true,
      } as T;
    } catch (err) {
      warnRebuildFailure(messages[i] ? messages[i].payload : null, err);
    }
  }
  return out || messages;
}

// ── roster peek (shared across the cards of one hydrate pass) ───────────────
// One roster fetch serves every connect card that hydrates within the TTL;
// a failed read is cached for the same window so an offline tab retries at
// a bounded rate instead of once per card per state change.
const ROSTER_CACHE_TTL_MS = 5_000;
let rosterCache: { at: number; promise: Promise<CustomConnection[] | null> } | null = null;

async function readCustomConnectionRoster(now: number = Date.now()): Promise<CustomConnection[] | null> {
  if (rosterCache && now - rosterCache.at < ROSTER_CACHE_TTL_MS) return rosterCache.promise;
  const promise = (async (): Promise<CustomConnection[] | null> => {
    try {
      const { listCustomConnections } = await import('../../composio/ComposioClient');
      const customs = await listCustomConnections();
      return Array.isArray(customs) ? customs : [];
    } catch {
      return null;
    }
  })();
  rosterCache = { at: now, promise };
  return promise;
}

/** Test seam: forget the shared roster read. */
export function __resetConnectRosterCacheForTests(): void {
  rosterCache = null;
}

/**
 * Peek the workspace's custom-connection roster for the card's app — the same
 * match offerBrowserLaneForApp uses (connectAppOfferKey over the slug, sans
 * its `custom-` prefix; the display name as a fallback). null when the roster
 * cannot be read: unknown, never "not connected".
 */
export async function peekConnectRoster(appName: string): Promise<ConnectRosterVerdict> {
  const wanted = connectAppOfferKey(appName);
  if (!wanted) return null;
  const customs = await readCustomConnectionRoster();
  if (customs === null) return null;
  const matches = customs.filter((c) => {
    const slugKey = connectAppOfferKey(String(c?.slug || '').replace(/^custom-/, ''));
    const nameKey = connectAppOfferKey(String(c?.name || ''));
    return slugKey === wanted || nameKey === wanted;
  });
  if (matches.length === 0) return { status: 'absent' };
  const connected = matches.find((c) => String(c.status || '') === 'connected');
  if (connected) return { status: 'connected', signedInAs: connected.signedInAs ?? null };
  const lost = matches.find((c) => String(c.status || '') === 'signin-lost');
  if (lost) return { status: 'signin-lost', signedInAs: lost.signedInAs ?? null };
  return { status: 'registered' };
}

/**
 * ASYNC hydrate refresh (mirrors rehydrateBatchTile): re-project the card
 * from its payload, consult the roster, and re-render the SAME message in
 * place with its current state. Never posts — no re-offer, no message.
 * Returns true when the roster answered (the tile is current); false when
 * nothing could be rebuilt or the roster was unreadable, so the caller's
 * once-per-id marker is released and a later pass retries. `current` lets
 * the lane skip a dispatch that would change nothing observable: an
 * unreadable roster never touches an intact card, and an identical
 * projection is not re-set.
 */
export async function rehydrateConnectTile(params: {
  messageId: string;
  threadId?: string | null;
  payload: ConnectCardPayloadLike;
  messaging: ConnectTileMessaging;
  current?: { workflowActionButtonsHtml?: string; isContextOnly?: boolean };
  lang?: UiLanguage;
  now?: Date;
}): Promise<boolean> {
  const { messageId, payload, messaging } = params;
  if (!messaging.updateMessageById || !messageId) return false;
  if (!isRebuildableConnectCard({ isUser: false, payload })) return false;
  const data = payloadData(payload);
  const currentHtml = typeof params.current?.workflowActionButtonsHtml === 'string'
    ? params.current.workflowActionButtonsHtml
    : '';
  const currentHidden = params.current?.isContextOnly === true;
  // Roster FIRST, rebuild after (review 2026-09-02): an unreadable roster
  // leaves an intact card untouched, and building the ~3.4 KB projection
  // before learning that would burn a string per card per messages change —
  // once per streamed chunk on a thread holding a card.
  const roster = await peekConnectRoster(requestedAppOf(payload));
  if (roster === null && currentHtml.trim() && !currentHidden) return false;
  const base = rebuildCardHtmlFromPayload(payload);
  if (!base) return false;
  // Same language the card body was posted in (payload snapshot); the UI
  // setting is the fallback for pre-C2 payloads only.
  const lang: UiLanguage = params.lang
    || (data.lang === 'es' || data.lang === 'en' ? (data.lang as UiLanguage) : memoizedUiLanguage());
  const status = connectLifecycleStatus({
    lifecycle: data.lifecycle as ConnectCardLifecycle | undefined,
    roster,
    lang,
    now: params.now,
  });
  const html = appendConnectLifecycleRow(base, status);
  if (html === currentHtml && !currentHidden) return roster !== null;
  messaging.updateMessageById(messageId, {
    workflowActionButtonsHtml: html,
    isContextOnly: false,
  });
  return roster !== null;
}

/**
 * LIFECYCLE `opened` stamp (contract C2), from the browser-lane click. Writes
 * `payload.data.lifecycle.opened` and NOTHING ELSE — the latest click wins,
 * because the row reports the most recent attempt.
 *
 * DELIBERATELY NOT the card html (2026-09-02 review). The tile renders its
 * card through `dangerouslySetInnerHTML` (SpecialMessageTile.tsx:1026), so a
 * new html string at click time REPLACES the button subtree — destroying the
 * imperative "opened ✓ + disabled" paint the click handler just applied and
 * handing the user a live pill again, one click into a flow that is already
 * running. The click owns the immediate feedback (the DOM paint); the payload
 * owns the durable record; the status row is rendered at HYDRATE from
 * lifecycle + the roster, which is exactly where contract C2 puts it. Writing
 * only `payload` also leaves the html string byte-identical, so React touches
 * no DOM at all on this update.
 *
 * Best-effort by design: no message id, no payload of a known kind, or no
 * updater → false, and the click flow proceeds untouched.
 */
export function stampConnectTileOpened(params: {
  messageId?: string | null;
  payload?: ConnectCardPayloadLike;
  updateMessageById?: ((id: string, update: Record<string, unknown>) => void) | null;
  lang?: UiLanguage;
  now?: Date;
}): boolean {
  const { messageId, payload, updateMessageById } = params;
  if (!messageId || typeof updateMessageById !== 'function') return false;
  if (!payload || !isRebuildableConnectCard({ isUser: false, payload })) return false;
  const now = params.now || new Date();
  const data = payloadData(payload);
  const prior = data.lifecycle && typeof data.lifecycle === 'object'
    ? (data.lifecycle as Record<string, unknown>)
    : {};
  const lifecycle: ConnectCardLifecycle = { ...prior, opened: now.toISOString() };
  updateMessageById(messageId, { payload: { ...payload, data: { ...data, lifecycle } } });
  return true;
}

/** The DOM node a click landed on — structural, so no lib.dom type is needed
 *  by callers that hand in a test double. */
export interface ClickedElementLike {
  closest?: (selector: string) => { getAttribute?: (name: string) => string | null } | null;
}

/**
 * The click-side half of the `opened` stamp, EXERCISABLE (review 2026-09-02:
 * the chain lived inline in a click handler and was pinned only as source
 * text — a pin that would pass unchanged while the runtime chain was broken,
 * which it had been: the reader it used was absent from the helper bundle a
 * click actually meets).
 *
 * The transcript surface is the caller: it holds the clicked tile's wrapper
 * (`data-message-id`), the live messages array, and the update dispatch. This
 * resolves the card from the first two and stamps through the third. Every
 * link is optional — no button, no wrapper, no messages, no updater, or a row
 * that is not a rebuildable connect card → false, and the sign-in flow it sits
 * beside proceeds untouched. Never throws.
 */
export function stampOpenedFromClick(params: {
  buttonEl?: ClickedElementLike | null;
  messages?: ReadonlyArray<{ id?: string; payload?: ConnectCardPayloadLike }> | null;
  updateMessageById?: ((id: string, update: Record<string, unknown>) => void) | null;
  now?: Date;
}): boolean {
  try {
    const { buttonEl, messages, updateMessageById } = params;
    if (typeof updateMessageById !== 'function' || !Array.isArray(messages)) return false;
    const wrapper = buttonEl && typeof buttonEl.closest === 'function'
      ? buttonEl.closest('[data-message-id]')
      : null;
    const messageId = wrapper && typeof wrapper.getAttribute === 'function'
      ? (wrapper.getAttribute('data-message-id') || '').trim()
      : '';
    if (!messageId) return false;
    const card = messages.find((m) => m && m.id === messageId);
    if (!card || !card.payload) return false;
    return stampConnectTileOpened({
      messageId,
      payload: card.payload,
      updateMessageById,
      ...(params.now ? { now: params.now } : {}),
    });
  } catch {
    // Best-effort by design — a missing tile/payload/dispatch never blocks a click.
    return false;
  }
}
